import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, Step, StepLabel, Stepper } from '@mui/material';
import TransportClinicStep from 'components/transport/add-transport/transport-clinic-step/transport-clinic-step.component';
import TransportWhenStep from 'components/transport/add-transport/transport-when-step/transport-when-step.component';
import TransportDetailsStep from 'components/transport/add-transport/transport-details-step/transport-details-step.component';
import { useMutation } from '@apollo/client';
import { AddTransportContext, emptyTransportOrderReq } from './add-transport-context';
import { TransportOrderRequest, TransportServices } from 'models/transport';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import moment from 'moment';
import { transportDateHelper } from 'utils/transport-date-helper';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { createTransportTemplate } from 'apollo/templates/transport';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

const AddTransport: FC = () => {
    const { t: translate } = useTranslation(['transport']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [showConfirmEditDialog, setShowConfirmEditDialog] = useState(false);
    const [navigateEditInfo, setNavigateEditInfo] = useState({ transportId: '', transportDate: new Date() });
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [transportOrderReq, setTransportOrderReq] = useState<TransportOrderRequest>(emptyTransportOrderReq());
    const shouldAddMoreRef = useRef(false);

    const [createTransport, { loading }] = useMutation(createTransportTemplate, {
        onCompleted: () => {
            localStorage.removeItem('transportLine');
            navigateOnPlacedOrder(shouldAddMoreRef.current);
        },
        onError: (error) => {
            const graphQLError = error.graphQLErrors?.[0];
            if (graphQLError?.message === 'TRANSPORT_ALREADY_EXISTS' && transportOrderReq.date) {
                setNavigateEditInfo({
                    transportId: (graphQLError.extensions?.exception as any)?.otherInfo?.transportId,
                    transportDate: new Date(transportOrderReq.date.setHours(0, 0, 0, 0)),
                });
                setShowConfirmEditDialog(true);
                return;
            }
            showBoundary(error);
        },
    });

    useEffect(() => {
        const transportType = type === 'pickup' ? TransportServices.PICKUP : TransportServices.DELIVERY;
        setTransportOrderReq({ ...{ ...transportOrderReq, serviceCode: transportType } });
    }, [type]);

    const pageTitle = () => {
        return transportOrderReq.serviceCode === TransportServices.PICKUP ? translate('PICKUP') : translate('DELIVERY');
    };

    const getSteps = () => {
        return [translate('CLINIC'), translate('WHEN'), translate('DETAILS')];
    };

    const navigateNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const navigateBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const placeOrder = (shouldAddMore: boolean) => {
        shouldAddMoreRef.current = shouldAddMore;
        const dateZeroTime = calculateZeroDateTime();
        createTransport({
            variables: {
                serviceCode: transportOrderReq.serviceCode,
                clinic: transportOrderReq.clinic?._id,
                transportDay: moment(dateZeroTime).format('YYYY-MM-DD'),
                manualPackageCount: transportOrderReq.manualPackageCount,
                comment: transportOrderReq.comment,
                internalMessage: transportOrderReq.internalMessage,
                deadline: transportOrderReq.deadline,
                userSelectedDate: moment(moment(transportOrderReq.date).toDate()).format('YYYY-MM-DD'),
            },
        });
    };

    const calculateZeroDateTime = () => {
        if (transportOrderReq.date) {
            if (transportOrderReq.serviceCode === TransportServices.DELIVERY) {
                return new Date(transportDateHelper.nextWorkingDay(transportOrderReq.date, false).setHours(0, 0, 0, 0));
            }
            return new Date(transportOrderReq.date.setHours(0, 0, 0, 0));
        }
        throw new Error('Transport date not set');
    };

    const navigateOnPlacedOrder = (shouldAddMore: boolean) => {
        setAppToasterContent({ isOpen: true, severity: 'success', message: translate('transport:ORDER_PLACED') });
        if (shouldAddMore) {
            setTransportOrderReq(emptyTransportOrderReq(transportOrderReq.serviceCode));
            setActiveStep(0);
            return;
        }
        navigateTransportList();
    };

    const navigateTransportList = (transportId?: string, date?: Date) => {
        let url = '/transport/transport-schedule';
        if (transportId && date) {
            url += `?editTransportId=${transportId}&date=${date.valueOf()}`;
        }
        navigate(url);
    };

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return <TransportClinicStep onNavigateNext={navigateNext} />;

            case 1:
                return <TransportWhenStep onNavigateNext={navigateNext} onNavigateBack={navigateBack} />;

            case 2:
                return (
                    <TransportDetailsStep loading={loading} onNavigateBack={navigateBack} onPlaceOrder={placeOrder} />
                );

            default:
                return 'Unknown stepIndex';
        }
    };

    return (
        <AddTransportContext.Provider value={{ transportOrderReq, setTransportOrderReq }}>
            <Box>
                <Box sx={{ margin: 'auto', maxWidth: '800px' }}>
                    <PageTitle headingStyle="h1" title={pageTitle()} />
                    <Paper>
                        <Box p={3}>
                            <Stepper activeStep={activeStep}>
                                {getSteps().map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <Box mt={4}>{getStepContent(activeStep)}</Box>
                        </Box>
                    </Paper>
                    <Box mt={2} sx={{ textAlign: 'center' }}>
                        <Button onClick={() => navigateTransportList()}>{translate('CANCEL_ORDER')}</Button>
                    </Box>
                </Box>
            </Box>
            <ConfirmDialog
                open={showConfirmEditDialog}
                handleClose={() => setShowConfirmEditDialog(false)}
                dialogTitle={translate('transport:CONFIRM_EDIT_TRANSPORT')}
                dialogContent={translate('transport:CONFIRM_EDIT_TRANSPORT_CONTENT')}
                handleConfirm={() => {
                    localStorage.removeItem('transportLine');
                    navigateTransportList(navigateEditInfo.transportId, navigateEditInfo.transportDate);
                }}
            />
        </AddTransportContext.Provider>
    );
};

export default AddTransport;
